const encoder = new TextEncoder();
const decoder = new TextDecoder();

const prohibitedPatterns = [
  { label: "adult content", pattern: /\b(porn|xxx|explicit sex|sexual service)\b/gi },
  { label: "illegal drugs", pattern: /\b(cocaine|heroin|meth|fentanyl|lsd|illegal drugs?)\b/gi },
  { label: "weapons", pattern: /\b(illegal gun|unregistered firearm|bomb|explosive)\b/gi },
  { label: "fraud", pattern: /\b(stolen card|fake id|money laundering|chargeback scam)\b/gi },
];

const toBase64 = (buffer) => btoa(String.fromCharCode(...new Uint8Array(buffer)));
const fromBase64 = (value) => Uint8Array.from(atob(value), (char) => char.charCodeAt(0));

export const censorMessage = (message) => {
  let censored = message;
  const flagged = [];
  prohibitedPatterns.forEach(({ label, pattern }) => {
    if (pattern.test(censored)) {
      flagged.push(label);
      censored = censored.replace(pattern, "[removed]");
    }
  });
  return { censored, flagged_terms: [...new Set(flagged)], was_censored: flagged.length > 0 };
};

export const ensureMessagingKeys = async (userId) => {
  const privateKeyName = `bonsai_private_key_${userId}`;
  const publicKeyName = `bonsai_public_key_${userId}`;
  const storedPrivate = localStorage.getItem(privateKeyName);
  const storedPublic = localStorage.getItem(publicKeyName);
  if (storedPrivate && storedPublic) return { privateJwk: JSON.parse(storedPrivate), publicJwk: JSON.parse(storedPublic) };

  const keyPair = await window.crypto.subtle.generateKey(
    { name: "RSA-OAEP", modulusLength: 2048, publicExponent: new Uint8Array([1, 0, 1]), hash: "SHA-256" },
    true,
    ["encrypt", "decrypt"]
  );
  const privateJwk = await window.crypto.subtle.exportKey("jwk", keyPair.privateKey);
  const publicJwk = await window.crypto.subtle.exportKey("jwk", keyPair.publicKey);
  localStorage.setItem(privateKeyName, JSON.stringify(privateJwk));
  localStorage.setItem(publicKeyName, JSON.stringify(publicJwk));
  return { privateJwk, publicJwk };
};

export const encryptForUser = async (publicJwk, message) => {
  const rsaKey = await window.crypto.subtle.importKey("jwk", publicJwk, { name: "RSA-OAEP", hash: "SHA-256" }, false, ["encrypt"]);
  const aesKey = await window.crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, true, ["encrypt", "decrypt"]);
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await window.crypto.subtle.encrypt({ name: "AES-GCM", iv }, aesKey, encoder.encode(message));
  const rawAes = await window.crypto.subtle.exportKey("raw", aesKey);
  const wrappedKey = await window.crypto.subtle.encrypt({ name: "RSA-OAEP" }, rsaKey, rawAes);
  return JSON.stringify({ v: 1, key: toBase64(wrappedKey), iv: toBase64(iv), data: toBase64(ciphertext) });
};

export const decryptForCurrentUser = async (privateJwk, ciphertext) => {
  try {
    const rsaKey = await window.crypto.subtle.importKey("jwk", privateJwk, { name: "RSA-OAEP", hash: "SHA-256" }, false, ["decrypt"]);
    const envelope = JSON.parse(ciphertext);
    const rawAes = await window.crypto.subtle.decrypt({ name: "RSA-OAEP" }, rsaKey, fromBase64(envelope.key));
    const aesKey = await window.crypto.subtle.importKey("raw", rawAes, { name: "AES-GCM" }, false, ["decrypt"]);
    const plaintext = await window.crypto.subtle.decrypt({ name: "AES-GCM", iv: fromBase64(envelope.iv) }, aesKey, fromBase64(envelope.data));
    return decoder.decode(plaintext);
  } catch {
    return "[Unable to decrypt on this device]";
  }
};
